import type { HelpCenterStructureNode } from '@/types';

export interface FlatNodeData {
  node: HelpCenterStructureNode;
  parentId: string | null;
  childrenIds: string[];
}

export interface FlatData {
  map: Map<string, FlatNodeData>;
  rootIds: string[];
}

/**
 * Get unique ID for a node
 */
export function getNodeId(node: HelpCenterStructureNode): string {
  return node.type === 'category'
    ? `category-${node.category!.id}`
    : `item-${node.item!.id}`;
}

/**
 * Convert nested structure to flat map for headless-tree
 */
export function flattenStructure(
  nodes: HelpCenterStructureNode[],
  parentId: string | null = null,
  flatMap: Map<string, FlatNodeData> = new Map()
): Map<string, FlatNodeData> {
  nodes.forEach((node) => {
    const nodeId = getNodeId(node);

    const childrenIds: string[] = [];
    if (node.children) {
      node.children.forEach((child) => {
        childrenIds.push(getNodeId(child));
      });
    }

    flatMap.set(nodeId, { node, parentId, childrenIds });

    if (node.children) {
      flattenStructure(node.children, nodeId, flatMap);
    }
  });

  return flatMap;
}

/**
 * Build flat data structure from nested nodes
 */
export function buildFlatData(structure: HelpCenterStructureNode[]): FlatData {
  const map = flattenStructure(structure);
  const rootIds: string[] = [];
  
  structure.forEach((node) => {
    const nodeId = getNodeId(node);
    const data = map.get(nodeId);
    if (data && data.parentId === null) {
      rootIds.push(nodeId);
    }
  });
  
  return { map, rootIds };
}

/**
 * Collect all category IDs from structure (for expansion)
 */
export function collectCategoryIds(nodes: HelpCenterStructureNode[]): string[] {
  const ids: string[] = [];
  nodes.forEach((node) => {
    if (node.type === 'category' && node.children && node.children.length > 0) {
      ids.push(`category-${node.category!.id}`);
      ids.push(...collectCategoryIds(node.children));
    }
  });
  return ids;
}

/**
 * Get path for a node (prefers backend-provided path)
 */
export function getNodePath(
  node: HelpCenterStructureNode,
  parentPathSegments: string[] = []
): string {
  // Use node.path if available (set by backend)
  if (node.path) {
    return node.path;
  }

  // Fallback: construct path manually
  if (node.type === 'category' && node.category?.slug) {
    return `/${[...parentPathSegments, node.category.slug].join('/')}`;
  }
  
  if (node.type === 'item') {
    if (node.item?.item_type === 'article' && node.item.article) {
      const articleSlug = node.item.article.slug || node.item.article.id;
      if (articleSlug) {
        return `/${[...parentPathSegments, articleSlug].join('/')}`;
      }
    } else if (node.item?.item_type === 'guide' && node.item.guide?.id) {
      return `/g/shared/${node.item.guide.id}`;
    }
  }
  
  return '';
}

/**
 * Get parent path segments for a node (excluding the node itself)
 */
export function getParentPathSegments(itemId: string, flatData: FlatData): string[] {
  const segments: string[] = [];
  const data = flatData.map.get(itemId);
  if (!data) return segments;

  let currentId: string | null = data.parentId;

  while (currentId) {
    const parentData = flatData.map.get(currentId);
    if (!parentData) break;

    if (parentData.node.type === 'category' && parentData.node.category?.slug) {
      segments.unshift(parentData.node.category.slug);
    }

    currentId = parentData.parentId;
  }

  return segments;
}
